import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, useApi } from "@/lib/api";
import { publicUrl } from "@/components/sites/helpers";

const Count = ({ v, max }) => <span className={`text-[10px] tabular-nums ${v > max ? "text-amber-700" : "text-slate-400"}`}>{v}/{max}</span>;

function PageRow({ pg, v, onChange }) {
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <li className="py-3" data-testid={`seo-page-${pg.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-slate-800">{pg.name} <code className="ml-1 text-[11px] font-normal text-slate-500">/{pg.home ? "" : pg.slug}</code>
          {pg.dynamic && <span className="ml-2 text-[11px] font-normal text-sky-700">{pg.routes > 1 ? `${pg.routes} pagine dai dati` : "pagine dai dati"}</span>}</p>
        <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={!!v.noindex} onCheckedChange={(x) => set("noindex", x)} data-testid={`seo-noindex-${pg.id}`} />Escludi da Google</label>
      </div>
      {pg.dynamic ? <p className="mt-1 text-[11px] text-slate-500">Titolo e descrizione di ogni scheda vengono dai suoi dati (es. «{pg.auto_title || "Nome · Sito"}»).</p> : (
        <div className="mt-2 grid gap-2 lg:grid-cols-2">
          <div><div className="flex justify-between"><Label className="text-[11px] text-slate-500">Titolo</Label><Count v={(v.title || pg.auto_title || "").length} max={60} /></div>
            <Input className="mt-0.5 h-9" value={v.title || ""} placeholder={pg.auto_title || "Automatico"} onChange={(e) => set("title", e.target.value)} maxLength={90} data-testid={`seo-title-${pg.id}`} /></div>
          <div><div className="flex justify-between"><Label className="text-[11px] text-slate-500">Descrizione</Label><Count v={(v.description || pg.auto_description || "").length} max={160} /></div>
            <Textarea className="mt-0.5 min-h-[38px] text-sm" rows={2} value={v.description || ""} placeholder={pg.auto_description || "Automatica (primo paragrafo della pagina)"} maxLength={300}
              onChange={(e) => set("description", e.target.value)} data-testid={`seo-desc-${pg.id}`} /></div>
        </div>
      )}
    </li>
  );
}

function SiteFields({ f, setF, data }) {
  const home = data.pages.find((p) => p.home) || {};
  const img = data.images.find((i) => i.id === f.image_asset_id);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div><Label className="text-xs">Nome del sito nei titoli</Label><Input className="mt-1" value={f.site_name} placeholder={(home.auto_title || "").split(" · ").pop() || "Nome del brand"}
        onChange={(e) => setF({ ...f, site_name: e.target.value })} maxLength={80} data-testid="seo-site-name" />
        <p className="mt-1 text-[11px] text-slate-500">Compare dopo il nome di ogni pagina: «Contatti · Nome».</p></div>
      <div className="sm:col-span-2"><Label className="text-xs">Descrizione del sito</Label><Textarea className="mt-1 text-sm" rows={2} value={f.description} maxLength={300} placeholder="Usata dalla homepage quando la sua descrizione è vuota"
        onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="seo-site-description" /></div>
      <div><Label className="text-xs">Immagine per i social</Label>
        <Select value={f.image_asset_id || "auto"} onValueChange={(v) => setF({ ...f, image_asset_id: v === "auto" ? null : v })}>
          <SelectTrigger className="mt-1" data-testid="seo-image-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="auto">Automatica: foto principale di ogni pagina</SelectItem>
            {data.images.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select>
        {img && <img src={publicUrl(img.url)} alt="" className="mt-2 h-16 rounded-md object-cover ring-1 ring-slate-200" data-testid="seo-image-preview" />}
        {!data.images.length && <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500"><ImageIcon size={11} />Carica immagini nel progetto per sceglierne una.</p>}</div>
    </div>
  );
}

export default function SeoSettings({ site, onSaved }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/sites/${site.id}/seo`).then(({ data: d }) => {
      setData(d);
      setF({ site_name: d.seo.site_name || "", description: d.seo.description || "", image_asset_id: d.seo.image_asset_id || null, pages: d.seo.pages || {} });
    }).catch((e) => toast.error(errMsg(e)));
  }, [api, site.id, site.updated_at]);
  if (!data || !f) return <Loader2 size={14} className="animate-spin text-slate-400" />;
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/sites/${site.id}`, { seo: f }); toast.success("Impostazioni SEO salvate: valgono dalla prossima compilazione"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const pages = data.pages.filter((p) => !p.home);
  const home = data.pages.find((p) => p.home);
  return (
    <section className="max-w-4xl space-y-4" data-testid="seo-settings">
      <div><h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Search size={14} className="text-sky-600" />SEO</h3>
        <p className="mt-0.5 text-xs text-slate-500">I campi vuoti usano i valori generati automaticamente (mostrati in grigio{data.deploy_number ? `, dalla compilazione n. ${data.deploy_number}` : ""}). Canonical, sitemap.xml e robots.txt sono sempre generati; anteprime e versioni restano noindex.</p></div>
      <SiteFields f={f} setF={setF} data={data} />
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 px-4" data-testid="seo-pages">
        {[home, ...pages].filter(Boolean).map((pg) => <PageRow key={pg.id} pg={pg} v={f.pages[pg.id] || {}} onChange={(v) => setF({ ...f, pages: { ...f.pages, [pg.id]: v } })} />)}
      </ul>
      <Button onClick={save} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="seo-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Salva SEO</Button>
    </section>
  );
}
